import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/@/components/ui/dialog";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Label } from "src/@/components/ui/label";
import { Plus, Trash2, GripVertical } from 'lucide-react';

const CORES_PREDEFINIDAS = [
  { valor: '#ef4444', nome: 'Vermelho' },
  { valor: '#f59e0b', nome: 'Amarelo' },
  { valor: '#22c55e', nome: 'Verde' },
  { valor: '#3b82f6', nome: 'Azul' },
  { valor: '#8b5cf6', nome: 'Violeta' },
  { valor: '#ec4899', nome: 'Rosa' },
  { valor: '#f97316', nome: 'Laranja' },
  { valor: '#06b6d4', nome: 'Ciano' },
  { valor: '#6b7280', nome: 'Cinza' },
  { valor: '#7C3AED', nome: 'Roxo' },
];

export default function ModalWorkflow({
  open,
  onClose,
  onSubmit,
  action,
  workflow,
}) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [estagios, setEstagios] = useState([{ nome: '', cor: '#6b7280' }]);
  const [saving, setSaving] = useState(false);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (workflow && action === 'Editar') {
      setNome(workflow.nome || '');
      setDescricao(workflow.descricao || '');
      setEstagios(workflow.estagios?.length > 0
        ? workflow.estagios.map(e => ({ ...e }))
        : [{ nome: '', cor: '#6b7280' }]
      );
    } else {
      setNome('');
      setDescricao('');
      setEstagios([{ nome: '', cor: '#6b7280' }]);
    }
  }, [workflow, action, open]);

  const handleAddEstagio = () => {
    setEstagios([...estagios, { nome: '', cor: '#7C3AED' }]);
  };

  const handleRemoveEstagio = (index) => {
    if (estagios.length <= 1) return;
    setEstagios(estagios.filter((_, i) => i !== index));
  };

  const handleEstagioChange = (index, field, value) => {
    const updated = [...estagios];
    updated[index] = { ...updated[index], [field]: value };
    setEstagios(updated);
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...estagios];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;
    setEstagios(reordered);
  };

  const handleSubmit = async () => {
    if (!nome.trim()) return;
    const validEstagios = estagios.filter(e => e.nome.trim());
    if (validEstagios.length === 0) return;

    setSaving(true);
    try {
      await onSubmit({
        nome: nome.trim(),
        descricao: descricao.trim(),
        estagios: validEstagios.map((e, i) => ({ ...e, ordem: i })),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isValid = nome.trim() && estagios.some(e => e.nome.trim());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {action === 'Criar' ? 'Novo Workflow' : 'Editar Workflow'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          {/* Nome */}
          <div>
            <Label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Nome
            </Label>
            <Input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Suporte, Desenvolvimento"
            />
          </div>

          {/* Descrição */}
          <div>
            <Label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Descrição (opcional)
            </Label>
            <Input
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Descreva o propósito deste workflow"
            />
          </div>

          {/* Estágios */}
          <div>
            <Label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Estágios
            </Label>

            <div className="space-y-2">
              {estagios.map((estagio, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl border border-gray-100 group"
                  draggable
                  onDragStart={() => { dragItem.current = index; }}
                  onDragEnter={() => { dragOverItem.current = index; }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <div className="cursor-grab text-gray-300 hover:text-gray-500">
                    <GripVertical size={16} />
                  </div>

                  <span className="text-xs text-gray-400 font-mono w-5 text-center">
                    {index + 1}
                  </span>

                  <Input
                    value={estagio.nome}
                    onChange={e => handleEstagioChange(index, 'nome', e.target.value)}
                    placeholder="Nome do estágio"
                    className="flex-1 h-8 text-sm"
                  />

                  {/* Color picker */}
                  <div className="flex items-center gap-1">
                    {CORES_PREDEFINIDAS.slice(0, 5).map((c) => (
                      <button
                        key={c.valor}
                        type="button"
                        onClick={() => handleEstagioChange(index, 'cor', c.valor)}
                        className={`w-5 h-5 rounded-full border transition-all ${
                          estagio.cor === c.valor
                            ? 'border-gray-900 scale-110 shadow-sm'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: c.valor }}
                        title={c.nome}
                      />
                    ))}
                    <div className="relative">
                      <input
                        type="color"
                        value={estagio.cor}
                        onChange={e => handleEstagioChange(index, 'cor', e.target.value)}
                        className="w-5 h-5 rounded-full cursor-pointer border border-gray-200 appearance-none bg-transparent"
                        style={{ backgroundColor: estagio.cor }}
                        title="Cor personalizada"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveEstagio(index)}
                    disabled={estagios.length <= 1}
                    className={`p-1 rounded-lg transition-all ${
                      estagios.length <= 1
                        ? 'text-gray-200 cursor-not-allowed'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddEstagio}
              className="mt-3 flex items-center gap-1.5 text-sm text-[#7C3AED] font-medium hover:underline"
            >
              <Plus size={14} />
              Adicionar Estágio
            </button>
          </div>

          {/* Preview */}
          <div>
            <Label className="text-sm text-gray-600 mb-2 block">Preview</Label>
            <div className="flex items-center gap-1 p-3 border border-gray-200 rounded-xl bg-gray-50 flex-wrap">
              {estagios.filter(e => e.nome.trim()).map((estagio, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full"
                    style={{ backgroundColor: `${estagio.cor}20`, color: estagio.cor }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: estagio.cor }}
                    />
                    {estagio.nome}
                  </span>
                  {i < estagios.filter(e => e.nome.trim()).length - 1 && (
                    <span className="text-gray-300 text-xs mx-0.5">→</span>
                  )}
                </div>
              ))}
              {estagios.filter(e => e.nome.trim()).length === 0 && (
                <span className="text-xs text-gray-400">Adicione estágios acima</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !isValid}
            className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
          >
            {action === 'Criar' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
